import { v4 as uuidv4 } from "uuid";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import checkBulletImage from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/UlListBlock/check_bullet.png";
import { getSalesBlockPrimaryStyleOptions } from "@circle-react-shared/BuilderUI/DefaultBlocks/Sales/constants";
import { createCardBlock } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/layoutBlockHelpers/cardBlockHelper";
import {
  createBadgeBlock,
  createButtonBlock,
  createH2Block,
  createH5Block,
  createPBlock,
  createUlBlock,
} from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/textBlocksHelpers";
import { getMarginOptions } from "@circle-react-shared/BuilderUI/utils/boxModelStyleHelpers";
import { getBorderColorOptions } from "@circle-react-shared/BuilderUI/utils/buttonStyleHelpers";
import { getTextAlignOptions } from "@circle-react-shared/BuilderUI/utils/textStyleHelpers";

interface Card {
  badge: string;
  buttonHref: string;
  buttonText: string;
  buttonType: "primaryButton" | "secondaryButton" | "customButton";
  description: string;
  features: string[];
  featuresTitle: string;
  footer?: string;
  header: string;
  highlight?: string;
  price: string;
}

const cards: Card[] = [
  {
    header: "Essentials",
    price: "$2,987",
    badge: "One time investment",
    description:
      "Perfect for self-motivated professionals who need guidance and structure.",
    featuresTitle: "Includes:",
    features: [
      "12 Weekly Group Coaching Calls",
      "Complete Video Module Library",
      "Private Community Access",
      "Basic Email Support",
      "Accountability System",
    ],
    buttonText: "Apply now",
    buttonHref: "#",
    buttonType: "secondaryButton",
  },
  {
    highlight: "Most Popular",
    header: "Breakthrough",
    price: "$3,987",
    badge: "One time investment",
    description:
      "Our most popular option for professionals seeking personalized transformation.",
    featuresTitle: "Includes:",
    features: [
      "12 Weekly Group Coaching Calls",
      "Strategic Planning Workshop",
      "Complete Video Module Library",
      "Private Community Access",
      "VIP Support Between Calls Sessions",
      "Personalised Resources & Tools",
      "Lifetime Access to Materials",
    ],
    buttonText: "Apply now",
    buttonHref: "#",
    buttonType: "primaryButton",
    footer: "Only 3 spots remaining this month",
  },
  {
    header: "VIP Experience",
    price: "$12,987",
    badge: "One time investment",
    description:
      "The ultimate coaching experience for executives and high-performers.",
    featuresTitle: "Includes:",
    features: [
      "Everything in Breakthrough",
      "Extended 6-month Support",
      "2 VIP Intensive Days",
      "24/7 Priority Access",
      "Custom Resource Creation",
      "Executive Network Access",
      "Quarterly Strategy Reviews",
    ],
    buttonText: "Apply now",
    buttonHref: "#",
    buttonType: "secondaryButton",
  },
];

export const defaultData: BaseBlock = {
  id: uuidv4(),
  type: "PricingBlock1",
  props: {
    header: {
      prop: "children",
      type: "children",
      value: [
        createBadgeBlock({
          text: "Our Pricing Plans",
        }),
        createH2Block({
          text: "Invest in your transformation",
        }),
        createPBlock({
          text: "Choose the option that best fits your goals and commitment level.",
        }),
      ],
    },
    content: {
      prop: "children",
      type: "children",
      value: cards.map(
        ({
          highlight,
          header,
          price,
          badge,
          description,
          featuresTitle,
          features,
          buttonText,
          buttonHref,
          buttonType,
          footer,
        }) =>
          createCardBlock({
            highlight,
            value: [
              createH5Block({ text: header }),
              createH2Block({
                text: price,
              }),
              createBadgeBlock({ text: badge }),
              createPBlock({
                text: description,
                paragraphStyle: "p3",
              }),
              createPBlock({
                text: featuresTitle,
                paragraphStyle: "p3",
              }),
              createUlBlock({
                items: features.map(feature => ({
                  text: feature,
                  placeholder: checkBulletImage,
                })),
              }),
              createButtonBlock({
                text: buttonText,
                href: buttonHref,
                buttonType,
                styles: {
                  margin: getMarginOptions({
                    defaultValue: {
                      marginTop: "30",
                      marginBottom: "0",
                      marginLeft: "0",
                      marginRight: "0",
                    },
                  }),
                },
              }),
              createPBlock({
                text: footer || "",
                paragraphStyle: "p3",
                styles: {
                  textAlign: getTextAlignOptions("center"),
                },
              }),
            ],
            styles: {
              borderColor: getBorderColorOptions({
                defaultValue: "#ffffff00",
              }),
            },
          }),
      ),
    },
    footer: {
      prop: "children",
      type: "children",
      value: [
        createPBlock({
          text: "Payment plans available. Schedule a free consultation to discuss options.",
          paragraphStyle: "p3",
        }),
        createButtonBlock({
          text: "Book your free strategy call",
          href: "#",
          buttonType: "primaryButton",
        }),
      ],
    },
    styles: {
      prop: "styles",
      type: "styles",
      value: {
        ...getSalesBlockPrimaryStyleOptions(),
      },
    },
  },
};
